import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { IMAGES, MaskLine } from "./anim";

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "35%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "60%"]);
  const opacity = useTransform(scrollYProgress, [0, 0.6], [1, 0]);

  return (
    <section
      ref={ref}
      data-testid="hero-section"
      className="relative h-[100svh] min-h-[680px] w-full overflow-hidden"
    >
      {/* Parallax background */}
      <motion.div style={{ y, scale }} className="absolute inset-0 -z-10">
        <img
          src={IMAGES.hero}
          alt=""
          aria-hidden="true"
          className="h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-black/35" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0B]/30 via-[#0A0A0B]/10 to-[#0A0A0B]" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_30%,rgba(0,229,255,0.08),transparent_55%)]" />
      </motion.div>

      {/* Top overline */}
      <motion.div
        style={{ opacity }}
        className="absolute top-28 md:top-32 left-6 md:left-10 right-6 md:right-10 flex justify-between font-mono text-[10px] md:text-xs uppercase tracking-[0.25em] text-[#A1A1AA]"
      >
        <MaskLine delay={0.5}>
          <span className="text-accent">◆</span> IT Consulting Studio
        </MaskLine>
        <MaskLine delay={0.6} className="hidden sm:block">
          Est. 2016 — Global
        </MaskLine>
      </motion.div>

      {/* Headline */}
      <motion.div
        style={{ y: textY, opacity }}
        className="absolute bottom-24 md:bottom-28 left-6 md:left-10 right-6 md:right-10"
      >
        <h1 className="font-heading font-black uppercase leading-[0.85] tracking-tighter text-[15vw] md:text-[13vw] lg:text-[11.5vw] text-glow">
          <MaskLine delay={0.35}>We Build.</MaskLine>
          <MaskLine delay={0.5}>
            We <span className="text-accent">Manage.</span>
          </MaskLine>
          <MaskLine delay={0.65}>We Scale.</MaskLine>
        </h1>

        <div className="mt-6 md:mt-8 flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1, duration: 0.8 }}
            className="max-w-md text-[#A1A1AA] text-base md:text-lg leading-relaxed"
          >
            Quadrim Tech is the technology partner for companies who refuse to
            settle. Managed IT and custom software, engineered without
            compromise.
          </motion.p>

          <motion.button
            data-testid="hero-cta"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.15, duration: 0.8 }}
            onClick={() =>
              document
                .getElementById("what")
                ?.scrollIntoView({ behavior: "smooth" })
            }
            className="group flex items-center gap-3 font-mono text-xs uppercase tracking-[0.2em] text-white w-fit"
          >
            <span className="grid h-14 w-14 place-items-center rounded-full border border-white/20 group-hover:border-accent group-hover:bg-accent group-hover:text-black transition-colors duration-300">
              <ArrowDown className="h-5 w-5 group-hover:translate-y-1 transition-transform duration-300" />
            </span>
            Explore
          </motion.button>
        </div>
      </motion.div>
    </section>
  );
}
